import { useTranslations } from 'next-intl';
import type React from 'react';

import { CountryCode } from '@cineamo/api/types/CountryCode';

import FskLabel from '../misc/fsk/FskLabel';

// The only FSK ratings that actually exist in Germany. Custom content (content without a linked
// movie) stores ageRating as free text, so it can contain placeholder values like "nicht bekannt" —
// those are not a real rating and must be treated the same as no rating at all (render nothing),
// consistent with how regular content behaves when no certification is found.
const KNOWN_FSK_AGE_RATINGS = ['0', '6', '12', '16', '18'];

export function isUnknownAgeRating(ageRating: string, countryCode: CountryCode): boolean {
    if (!ageRating) {
        return true;
    }
    return countryCode?.toUpperCase() === 'DE' && !KNOWN_FSK_AGE_RATINGS.includes(ageRating);
}

export function useAgeRatingText(ageRating: string, countryCode: CountryCode): string | undefined {
    const t = useTranslations();

    if (isUnknownAgeRating(ageRating, countryCode)) {
        return undefined;
    }

    if (countryCode && t.has(`movie.age-rating.${countryCode.toLowerCase()}`)) {
        return t(`movie.age-rating.${countryCode.toLowerCase()}`, { ageRating: ageRating });
    }

    return t('movie.age-rating.from', { ageRating: ageRating });
}

export function getAgeRatingIcon(
    ageRating: string,
    countryCode: CountryCode,
    options?: {
        onClick?: () => void;
        useInfoModal?: boolean;
        isThemeLight?: boolean;
        classNameIcon?: string;
        /** Reported with the badge's click event. */
        movieTitle?: string;
        /** Numeric movie id; when given, opening the info modal also reports a pulse view. */
        movieId?: number;
    }
): React.JSX.Element | null {
    if (isUnknownAgeRating(ageRating, countryCode)) {
        return null;
    }

    switch (countryCode?.toUpperCase()) {
        case 'DE':
            return (
                <FskLabel
                    ageRating={ageRating}
                    movieTitle={options?.movieTitle}
                    movieId={options?.movieId}
                    onClick={options?.onClick}
                    useInfoModal={options?.useInfoModal}
                    isThemeLight={options?.isThemeLight}
                    options={{
                        classNameIcon: options?.classNameIcon
                    }}
                />
            );
        default: {
            return null;
        }
    }
}
